<template>
  <section class="financial-summary-tab">
    <div class="financial-summary-top">
      <div class="financial-summary-top__cards">
        <FinancialAccountCard
          v-for="account in summaryCards"
          :key="account.id"
          :text="text"
          :account="account"
          :show-menu="true"
          @edit="handleCardEdit"
          @transfer="handleCardTransfer"
          @toggle-status="handleCardToggleStatus"
          @movements="handleCardMovements"
          @open-account="handleCardOpenAccount"
        />

        <article v-if="showAddCard" class="customer-detail-card financial-summary-link-card">
          <button type="button" class="financial-summary-link-card__button">
            <span class="financial-summary-link-card__icon"><i class="fa fa-plus" aria-hidden="true"></i></span>
            <strong>{{ text.summary.addCard.title }}</strong>
            <small>{{ text.summary.addCard.subtitle }}</small>
          </button>
        </article>
      </div>

      <aside class="financial-summary-top__distribution customer-detail-card customer-detail-card--accent-cyan">
        <header class="customer-detail-card-header customer-detail-card-header--inline">
          <h4>{{ text.summary.distributionTitle }}</h4>
          <small>{{ text.summary.distributionTotal }}</small>
        </header>

        <div class="financial-distribution__total-bar">
          <span
            v-for="item in distribution"
            :key="item.id"
            :style="{ width: `${item.percent}%`, background: item.color }"
          ></span>
        </div>

        <ul class="financial-distribution__list">
          <li v-for="item in distribution" :key="`item-${item.id}`">
            <div>
              <i class="fa fa-circle" :style="{ color: item.color }" aria-hidden="true"></i>
              <span>{{ item.name }}</span>
            </div>
            <strong>{{ item.percentLabel }}</strong>
          </li>
        </ul>
      </aside>
    </div>

    <article class="customer-detail-card customer-detail-card--accent-success financial-summary-movements">
      <header class="financial-summary-movements__head">
        <h4>{{ text.summary.tableTitle }}</h4>
      </header>

      <div class="app-table-wrap">
        <div class="app-table-scroll">
          <table class="app-data-table">
            <thead>
              <tr>
                <th>{{ text.summary.table.date }}</th>
                <th>{{ text.summary.table.account }}</th>
                <th>{{ movementTypeHeader }}</th>
                <th>{{ text.summary.table.concept }}</th>
                <th class="app-text-center financial-col-center">{{ text.summary.table.amount }}</th>
              </tr>
            </thead>
            <tbody>
              <tr v-if="filteredMovements.length === 0">
                <td colspan="5" class="app-table-empty">{{ text.actions.table.empty }}</td>
              </tr>
              <tr v-for="movement in filteredMovements" :key="movement.id">
                <td>
                  <div class="app-strong">{{ movement.date }}</div>
                  <small class="app-muted">{{ movement.time }}</small>
                </td>
                <td>
                  <div class="app-strong">
                    <a
                      class="financial-summary-movements__link"
                      :href="resolveMovementHref(movement, 'movements')"
                      @click.prevent="openMovementAccount(movement, 'movements')"
                    >
                      {{ movement.account }}
                    </a>
                  </div>
                  <small class="app-muted">{{ movement.accountMeta }}</small>
                </td>
                <td>
                  <span
                    v-if="movement.category"
                    class="financial-category-pill"
                    :class="resolveCategoryClass(movement.categoryKey || movement.typeReference)"
                  >
                    {{ movement.category }}
                  </span>
                </td>
                <td>
                  <div class="app-strong">
                    <a
                      class="financial-summary-movements__link"
                      :href="resolveMovementHref(movement, 'movements')"
                      @click.prevent="openMovementAccount(movement, 'movements')"
                    >
                      {{ movement.concept }}
                    </a>
                  </div>
                </td>
                <td class="app-text-right">
                  <strong :class="movement.isIncome ? 'financial-amount-positive' : 'financial-amount-negative'">
                    {{ movement.amountLabel }}
                  </strong>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </article>
  </section>
</template>

<script>
import FinancialAccountCard from '@/components/financial/cards/FinancialAccountCard.vue';

export default {
  name: 'FinancialSummaryTab',
  components: {
    FinancialAccountCard,
  },
  props: {
    text: {
      type: Object,
      required: true,
    },
    summaryCards: {
      type: Array,
      default: () => [],
    },
    showAddCard: {
      type: Boolean,
      default: false,
    },
    distribution: {
      type: Array,
      default: () => [],
    },
    movements: {
      type: Array,
      default: () => [],
    },
  },
  emits: ['movements', 'edit-account', 'transfer-account', 'toggle-account-status', 'open-account'],
  computed: {
    filteredMovements() {
      return Array.isArray(this.movements) ? this.movements : [];
    },
    movementTypeHeader() {
      return String(
        this.text?.detailAccount?.movements?.table?.movementType
        || this.text?.summary?.table?.category
        || 'Tipo de movimiento'
      ).trim();
    },
  },
  methods: {
    handleCardMovements(account) {
      this.$emit('movements', account);
    },
    handleCardOpenAccount(account) {
      this.$emit('open-account', account);
    },
    handleCardEdit(account) {
      this.$emit('edit-account', account);
    },
    handleCardTransfer(account) {
      this.$emit('transfer-account', account);
    },
    handleCardToggleStatus(account) {
      this.$emit('toggle-account-status', account);
    },
    resolveMovementHref(movement, tab = 'movements') {
      const accountId = Number(movement?.financialAccountId || movement?.financial_account_id || 0);
      if (accountId <= 0 || !this.$router) {
        return '#';
      }

      return this.$router.resolve({
        name: 'financialAccountDetail',
        params: { financialAccountId: String(accountId) },
        query: { tab },
      }).href;
    },
    openMovementAccount(movement, tab = 'movements') {
      const accountId = Number(movement?.financialAccountId || movement?.financial_account_id || 0);
      if (accountId <= 0) {
        return;
      }

      this.$router.push({
        name: 'financialAccountDetail',
        params: { financialAccountId: String(accountId) },
        query: { tab },
      });
    },
    resolveCategoryClass(categoryKey) {
      const normalized = String(categoryKey || '').trim().toLowerCase();
      if (normalized === 'income') return 'financial-category-pill--income';
      if (normalized === 'expense') return 'financial-category-pill--expense';
      if (normalized === 'transfer') return 'financial-category-pill--transfer';
      if (normalized === 'init') return 'financial-category-pill--init';
      if (normalized === 'otherincome') return 'financial-category-pill--otherincome';
      if (normalized === 'otherexpense') return 'financial-category-pill--otherexpense';
      return 'financial-category-pill--neutral';
    },
  },
};
</script>

<style scoped>
.app-data-table thead th.financial-col-center {
  text-align: center !important;
}

.financial-summary-tab {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.financial-summary-top {
  display: grid;
  grid-template-columns: minmax(0, 2fr) minmax(280px, 1fr);
  gap: 12px;
  align-items: stretch;
}

.financial-summary-top__cards {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 12px;
  align-items: stretch;
}

.financial-summary-top__cards > article {
  height: 100%;
  min-height: 184px;
}

.financial-summary-account__head {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 8px;
  position: relative;
}

.financial-summary-movements__link {
  color: inherit;
  text-decoration: none;
}

.financial-summary-movements__link:hover {
  color: #0b63b4;
  text-decoration: underline;
}

.financial-summary-account {
  border: 1px solid #cfe0f5;
}

.financial-summary-account__title-wrap {
  display: inline-flex;
  align-items: center;
  gap: 10px;
}

.financial-summary-account__title-wrap h4 {
  margin: 0;
  font-size: 16px;
}

.financial-summary-account__title-wrap p {
  margin: 2px 0 0;
  color: #64748b;
  font-size: 12px;
}

.financial-summary-account__badge {
  min-width: 36px;
  height: 34px;
  border-radius: 9px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: #ffffff;
  font-size: 11px;
  font-weight: 700;
}

.financial-summary-account__badge.is-san {
  background: #dc2626;
}

.financial-summary-account__badge.is-usd {
  background: #7f1d1d;
}

.financial-summary-account__badge.is-cc {
  background: #f59e0b;
}

.financial-summary-account__badge.is-bb {
  background: #2563eb;
}

.financial-summary-account__badge.is-sp {
  background: #7c3aed;
}

.financial-summary-account__badge.is-sv {
  background: #0f766e;
}

.financial-summary-account__menu-btn {
  border: 0;
  background: transparent;
  color: #64748b;
  width: 28px;
  height: 28px;
  border-radius: 6px;
}

.financial-summary-account__menu-btn:hover {
  background: rgba(15, 23, 42, 0.08);
  color: #0f172a;
}

.financial-summary-account__body {
  margin-top: 12px;
}

.financial-summary-account__body small {
  display: block;
  color: #64748b;
  font-size: 12px;
}

.financial-summary-account__body strong {
  font-size: 21px;
  line-height: 1.05;
  color: #0f172a;
}

.financial-summary-account__body em {
  display: block;
  font-style: normal;
  color: #94a3b8;
  font-size: 13px;
  font-weight: 600;
  margin-top: 4px;
}

.financial-summary-account__body span {
  font-size: 11px;
  color: #64748b;
}

.financial-summary-account__footer {
  margin-top: 12px;
  padding-top: 10px;
  border-top: 1px solid rgba(148, 163, 184, 0.28);
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.financial-summary-account__link-btn {
  border: 0;
  background: transparent;
  color: #0070cd;
  font-size: 12px;
  font-weight: 700;
  display: inline-flex;
  align-items: center;
  gap: 6px;
}

.financial-summary-account__menu {
  min-width: 130px;
  top: calc(100% + 4px);
}

.financial-summary-link-card {
  border-style: dashed;
  border-color: rgba(148, 163, 184, 0.5);
  background: linear-gradient(135deg, rgba(239, 246, 255, 0.55), rgba(248, 250, 252, 0.92));
}

.financial-summary-link-card__button {
  width: 100%;
  height: 100%;
  border: 0;
  background: transparent;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  gap: 8px;
  text-align: center;
  min-height: 100%;
}

.financial-summary-link-card__icon {
  width: 42px;
  height: 42px;
  border-radius: 999px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: #0070cd;
  background: #e0ecff;
}

.financial-summary-link-card__button strong {
  color: #0f172a;
}

.financial-summary-link-card__button small {
  max-width: 250px;
  color: #64748b;
}

.financial-summary-top__distribution h4 {
  margin: 0;
}

.financial-summary-top__distribution {
  height: 100%;
  display: flex;
  flex-direction: column;
}

.financial-summary-top__distribution small {
  color: #64748b;
  font-size: 12px;
  text-transform: uppercase;
  font-weight: 700;
}

.financial-distribution__total-bar {
  margin-top: 10px;
  height: 11px;
  border-radius: 999px;
  overflow: hidden;
  display: flex;
  background: #e2e8f0;
}

.financial-distribution__list {
  list-style: none;
  margin: 10px 0 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.financial-distribution__list li {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
}

.financial-distribution__list li > div {
  display: inline-flex;
  align-items: center;
  gap: 7px;
  color: #334155;
  font-size: 13px;
}

.financial-distribution__list strong {
  color: #0f172a;
  font-size: 13px;
}

.financial-summary-movements__head h4 {
  margin: 0;
}

.financial-category-pill {
  display: inline-flex;
  align-items: center;
  min-height: 24px;
  border-radius: 999px;
  padding: 0 10px;
  font-size: 11px;
  border: 1px solid transparent;
}

.financial-category-pill--income {
  color: #14532d;
  background: #dcfce7;
  border-color: #86efac;
}

.financial-category-pill--expense {
  color: #9f1239;
  background: #ffe4e6;
  border-color: #fda4af;
}

.financial-category-pill--transfer {
  color: #1e3a8a;
  background: #dbeafe;
  border-color: #93c5fd;
}

.financial-category-pill--init {
  color: #6b21a8;
  background: #f3e8ff;
  border-color: #d8b4fe;
}

.financial-category-pill--otherincome {
  color: #065f46;
  background: #ccfbf1;
  border-color: #99f6e4;
}

.financial-category-pill--otherexpense {
  color: #7c2d12;
  background: #ffedd5;
  border-color: #fdba74;
}

.financial-category-pill--neutral {
  color: #334155;
  background: #e2e8f0;
  border-color: #cbd5e1;
}

.financial-amount-positive {
  color: #16a34a;
}

.financial-amount-negative {
  color: #dc2626;
}

@media (max-width: 1200px) {
  .financial-summary-top {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 900px) {
  .financial-summary-top__cards {
    grid-template-columns: 1fr;
  }
}
</style>
